import { inject, Pipe, PipeTransform } from '@angular/core';
import { Database, onValue, ref } from '@angular/fire/database';
import { hotelFeature } from '@contler/core/hotel';
import { translateFeature } from '@contler/translate/state';
import { getActualLan } from '@contler/translate/utils';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, filter, map, Observable, of, switchMap } from 'rxjs';

import { getFireLoc } from '../utils/get-language';

@Pipe({
  name: 'dynamicTranslate',
  standalone: true,
})
export class DynamicTranslatePipe implements PipeTransform {
  private db = inject(Database);
  private store = inject(Store);
  private translate = inject(TranslateService);

  transform(value: string | undefined, hotelUid?: string | undefined | null): Observable<string> {
    if (!value || !this.validateKey(value)) {
      return of(value ?? '');
    }

    return this.getTranslation(value, hotelUid).pipe();
  }

  validateKey(key: string): boolean {
    return !key.includes('.') && !key.includes('#') && !key.includes('$') && !key.includes('[') && !key.includes(']');
  }

  private getTranslation(key: string, hotelUid?: string | undefined | null): Observable<string> {
    if (hotelUid) {
      return this.store.select(translateFeature.selectActualLanguage).pipe(
        switchMap((language) => {
          const lang = language?.prefix ?? getFireLoc(this.translate.currentLang ?? getActualLan());
          if (lang.includes('pt')) {
            const refPt = ref(this.db, `dictionary/${hotelUid}/${key}/pt-PT`);
            const refBr = ref(this.db, `dictionary/${hotelUid}/${key}/pt-BR`);
            return combineLatest([
              new Observable<string | null>((observer) => {
                const unsubscribe = onValue(refPt, (snap) => {
                  observer.next(snap.val() ?? null);
                });
                return () => unsubscribe();
              }),
              new Observable<string | null>((observer) => {
                const unsubscribe = onValue(refBr, (snap) => {
                  observer.next(snap.val() ?? null);
                });
                return () => unsubscribe();
              }),
            ]).pipe(map(([pt, br]) => pt || br || key));
          } else {
            const refT = ref(this.db, `dictionary/${hotelUid}/${key}/${lang}`);
            return new Observable<string>((observer) => {
              const unsubscribe = onValue(refT, (snap) => {
                observer.next(snap.val() || key);
              });
              return () => unsubscribe();
            });
          }
        }),
      );
    }
    return combineLatest([
      this.store.select(hotelFeature.selectHotel),
      this.store.select(translateFeature.selectActualLanguage),
    ]).pipe(
      filter(([hotel]) => !!hotel),
      switchMap(([hotel, language]) => {
        const lang = language?.prefix ?? getFireLoc(this.translate.currentLang ?? getActualLan());
        if (lang.includes('pt')) {
          const refPt = ref(this.db, `dictionary/${hotel!.uid}/${key}/pt-PT`);
          const refBr = ref(this.db, `dictionary/${hotel!.uid}/${key}/pt-BR`);
          return combineLatest([
            new Observable<string | null>((observer) => {
              const unsubscribe = onValue(refPt, (snap) => {
                observer.next(snap.val() ?? null);
              });
              return () => unsubscribe();
            }),
            new Observable<string | null>((observer) => {
              const unsubscribe = onValue(refBr, (snap) => {
                observer.next(snap.val() ?? null);
              });
              return () => unsubscribe();
            }),
          ]).pipe(map(([pt, br]) => pt || br || key));
        } else {
          const refT = ref(this.db, `dictionary/${hotel!.uid}/${key}/${lang}`);
          return new Observable<string>((observer) => {
            const unsubscribe = onValue(refT, (snap) => {
              observer.next(snap.val() || key);
            });
            return () => unsubscribe();
          });
        }
      }),
    );
  }
}
